'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'bn';

interface Translations {
  // Common
  prime: string;
  verified: string;
  online: string;
  // Landing
  tagline: string;
  subTagline: string;
  getStarted: string;
  alreadyHaveAccount: string;
  termsText: string;
  featurePills: string[];
  // Gender
  iAmA: string;
  genderPersonalize: string;
  woman: string;
  womanFree: string;
  womanDesc: string;
  man: string;
  manFree: string;
  manDesc: string;
  nonBinary: string;
  nonBinaryDesc: string;
  // Auth
  welcomeBack: string;
  createAccount: string;
  continueGoogle: string;
  continueGithub: string;
  emailLabel: string;
  emailPlaceholder: string;
  passwordLabel: string;
  signIn: string;
  signUp: string;
  noAccount: string;
  haveAccount: string;
  forgotPassword: string;
  emailRequired: string;
  emailInvalid: string;
  passwordRequired: string;
  passwordMin: string;
  // Onboarding
  whatsYourName: string;
  nameDesc: string;
  namePlaceholder: string;
  nameRequired: string;
  continueBtn: string;
  whatsYourDob: string;
  dobDesc: string;
  addPhotos: string;
  photosDesc: string;
  uploadPhoto: string;
  skip: string;
  yourBio: string;
  bioDesc: string;
  bioPlaceholder: string;
  finishProfile: string;
  // Swipe
  discover: string;
  swipesRemaining: string;
  superLike: string;
  boost: string;
  boostActive: string;
  noMoreProfiles: string;
  noMoreDesc: string;
  refreshProfiles: string;
  kmAway: string;
  // Matches
  matches: string;
  likesYou: string;
  newMatches: string;
  searchMatches: string;
  noMatchesYet: string;
  noMatchesDesc: string;
  startSwiping: string;
  // Messaging
  messages: string;
  unread: string;
  searchConversations: string;
  noConversations: string;
  viewMatches: string;
  typeMessage: string;
  // Profile
  profile: string;
  discovery: string;
  notifications: string;
  account: string;
  photos: string;
  basicInfo: string;
  displayName: string;
  bio: string;
  jobTitle: string;
  height: string;
  education: string;
  hometown: string;
  interests: string;
  saveChanges: string;
  saving: string;
  // Subscription
  unlockPotential: string;
  subscriptionDesc: string;
  usedSwipes: string;
  usedSwipesDesc: string;
  peopleLikedYou: string;
  waiting: string;
  choosePlan: string;
  perMonth: string;
  startSwiping2: string;
  seeWhoLiked: string;
  welcomeToPrime: string;
  primeActive: string;
  // BottomNav
  navDiscover: string;
  navMatches: string;
  navMessages: string;
  navProfile: string;
}

const en: Translations = {
  prime: 'Prime',
  verified: 'Verified',
  online: 'Online',
  tagline: 'মনের মানুষ',
  subTagline: 'Have you found your\ntrue love ?',
  getStarted: 'Get Started',
  alreadyHaveAccount: 'Already have an account? Sign in',
  termsText: 'By continuing, you agree to our Terms of Service and Privacy Policy.',
  featurePills: ['💝 Swipe & Match', '💬 Real Chat', '🔒 Safe & Verified', '🌍 Find Nearby'],
  iAmA: 'I am a…',
  genderPersonalize: 'This helps us personalize your experience.',
  woman: 'Woman',
  womanFree: 'FREE Forever',
  womanDesc: 'Unlimited swipes, matches & messages. Always free.',
  man: 'Man',
  manFree: '10 free swipes',
  manDesc: '10 free swipes daily. Upgrade to Prime for unlimited access.',
  nonBinary: 'Non-binary / Other',
  nonBinaryDesc: 'Free access. Express yourself authentically.',
  welcomeBack: 'Welcome back',
  createAccount: 'Create account',
  continueGoogle: 'Continue with Google',
  continueGithub: 'Continue with GitHub',
  emailLabel: 'Email',
  emailPlaceholder: 'your@email.com',
  passwordLabel: 'Password',
  signIn: 'Sign In',
  signUp: 'Create Account',
  noAccount: "Don't have an account? Sign up",
  haveAccount: 'Already have an account? Sign in',
  forgotPassword: 'Forgot password?',
  emailRequired: 'Email is required',
  emailInvalid: 'Enter a valid email',
  passwordRequired: 'Password is required',
  passwordMin: 'Minimum 6 characters',
  whatsYourName: "What's your name?",
  nameDesc: 'This is how you\'ll appear to others.',
  namePlaceholder: 'Your first name',
  nameRequired: 'Name is required',
  continueBtn: 'Continue',
  whatsYourDob: 'When were you born?',
  dobDesc: 'Your age will be shown on your profile.',
  addPhotos: 'Add your photos',
  photosDesc: 'Add at least 2 photos to get 3× more matches.',
  uploadPhoto: 'Upload Photo',
  skip: 'Skip for now',
  yourBio: 'Write your bio',
  bioDesc: 'Tell people what makes you unique.',
  bioPlaceholder: 'I love chai, long walks, and good conversations…',
  finishProfile: 'Finish & Start Matching 🌹',
  discover: 'Discover',
  swipesRemaining: 'swipes left',
  superLike: 'Super Like',
  boost: 'Boost',
  boostActive: 'Boost Active',
  noMoreProfiles: 'You\'ve seen everyone!',
  noMoreDesc: 'Check back later for new profiles in your area.',
  refreshProfiles: 'Refresh Profiles',
  kmAway: 'km away',
  matches: 'Matches',
  likesYou: 'Likes You',
  newMatches: 'New Matches',
  searchMatches: 'Search matches...',
  noMatchesYet: 'No matches yet',
  noMatchesDesc: 'Keep swiping — your match is out there.',
  startSwiping: 'Start Swiping',
  messages: 'Messages',
  unread: 'unread',
  searchConversations: 'Search conversations...',
  noConversations: 'No conversations yet',
  viewMatches: 'View your matches →',
  typeMessage: 'Type a message…',
  profile: 'Profile',
  discovery: 'Discovery',
  notifications: 'Notifications',
  account: 'Account',
  photos: 'Photos',
  basicInfo: 'Basic Info',
  displayName: 'Display Name',
  bio: 'Bio',
  jobTitle: 'Job Title',
  height: 'Height',
  education: 'Education',
  hometown: 'Hometown',
  interests: 'Interests',
  saveChanges: 'Save Changes',
  saving: 'Saving…',
  unlockPotential: 'Unlock your full potential',
  subscriptionDesc: 'See who likes you, unlimited swipes, and premium features that get you more matches.',
  usedSwipes: "You've used all 10 free swipes",
  usedSwipesDesc: 'Upgrade to Prime to keep swiping and see who already liked you.',
  peopleLikedYou: 'People who liked you',
  waiting: 'waiting',
  choosePlan: 'Choose your plan',
  perMonth: '/mo',
  startSwiping2: 'Start Swiping 🌹',
  seeWhoLiked: 'See who liked me →',
  welcomeToPrime: 'Welcome to Prime',
  primeActive: 'Your subscription is now active.',
  navDiscover: 'Discover',
  navMatches: 'Matches',
  navMessages: 'Messages',
  navProfile: 'Profile',
};

const bn: Translations = {
  prime: 'প্রাইম',
  verified: 'যাচাইকৃত',
  online: 'অনলাইন',
  tagline: 'মনের মানুষ',
  subTagline: 'তুমি কি তোমার\nসত্যিকারের ভালোবাসা খুঁজে পেয়েছ ?',
  getStarted: 'শুরু করুন',
  alreadyHaveAccount: 'ইতিমধ্যে অ্যাকাউন্ট আছে? সাইন ইন করুন',
  termsText: 'চালিয়ে যাওয়ার মাধ্যমে, আপনি আমাদের সেবার শর্তাবলী ও গোপনীয়তা নীতিতে সম্মত হচ্ছেন।',
  featurePills: ['💝 সোয়াইপ ও ম্যাচ', '💬 রিয়েল চ্যাট', '🔒 নিরাপদ ও যাচাইকৃত', '🌍 কাছের মানুষ খুঁজুন'],
  iAmA: 'আমি একজন…',
  genderPersonalize: 'এটি আপনার অভিজ্ঞতা ব্যক্তিগতকৃত করতে সাহায্য করে।',
  woman: 'নারী',
  womanFree: 'সম্পূর্ণ বিনামূল্যে',
  womanDesc: 'সীমাহীন সোয়াইপ, ম্যাচ ও বার্তা। সবসময় বিনামূল্যে।',
  man: 'পুরুষ',
  manFree: '১০টি বিনামূল্যে সোয়াইপ',
  manDesc: 'প্রতিদিন ১০টি বিনামূল্যে সোয়াইপ। সীমাহীন অ্যাক্সেসের জন্য প্রাইমে আপগ্রেড করুন।',
  nonBinary: 'নন-বাইনারি / অন্যান্য',
  nonBinaryDesc: 'বিনামূল্যে অ্যাক্সেস। নিজেকে প্রকাশ করুন।',
  welcomeBack: 'স্বাগতম ফিরে',
  createAccount: 'অ্যাকাউন্ট তৈরি করুন',
  continueGoogle: 'গুগল দিয়ে চালিয়ে যান',
  continueGithub: 'গিটহাব দিয়ে চালিয়ে যান',
  emailLabel: 'ইমেইল',
  emailPlaceholder: 'আপনার@ইমেইল.com',
  passwordLabel: 'পাসওয়ার্ড',
  signIn: 'সাইন ইন',
  signUp: 'অ্যাকাউন্ট তৈরি করুন',
  noAccount: 'অ্যাকাউন্ট নেই? সাইন আপ করুন',
  haveAccount: 'ইতিমধ্যে অ্যাকাউন্ট আছে? সাইন ইন করুন',
  forgotPassword: 'পাসওয়ার্ড ভুলে গেছেন?',
  emailRequired: 'ইমেইল প্রয়োজন',
  emailInvalid: 'সঠিক ইমেইল দিন',
  passwordRequired: 'পাসওয়ার্ড প্রয়োজন',
  passwordMin: 'কমপক্ষে ৬ অক্ষর',
  whatsYourName: 'আপনার নাম কী?',
  nameDesc: 'অন্যরা আপনাকে এই নামে দেখবে।',
  namePlaceholder: 'আপনার প্রথম নাম',
  nameRequired: 'নাম প্রয়োজন',
  continueBtn: 'চালিয়ে যান',
  whatsYourDob: 'আপনার জন্ম তারিখ কী?',
  dobDesc: 'আপনার বয়স প্রোফাইলে দেখানো হবে।',
  addPhotos: 'আপনার ছবি যোগ করুন',
  photosDesc: '৩× বেশি ম্যাচ পেতে কমপক্ষে ২টি ছবি যোগ করুন।',
  uploadPhoto: 'ছবি আপলোড করুন',
  skip: 'এখনের জন্য এড়িয়ে যান',
  yourBio: 'আপনার বায়ো লিখুন',
  bioDesc: 'মানুষকে বলুন আপনি কী অনন্য।',
  bioPlaceholder: 'আমি চা, দীর্ঘ হাঁটা এবং ভালো কথোপকথন ভালোবাসি…',
  finishProfile: 'শেষ করুন ও ম্যাচিং শুরু করুন 🌹',
  discover: 'আবিষ্কার',
  swipesRemaining: 'সোয়াইপ বাকি',
  superLike: 'সুপার লাইক',
  boost: 'বুস্ট',
  boostActive: 'বুস্ট সক্রিয়',
  noMoreProfiles: 'সবাইকে দেখা হয়ে গেছে!',
  noMoreDesc: 'নতুন প্রোফাইলের জন্য পরে আবার দেখুন।',
  refreshProfiles: 'প্রোফাইল রিফ্রেশ করুন',
  kmAway: 'কিমি দূরে',
  matches: 'ম্যাচ',
  likesYou: 'আপনাকে পছন্দ করেছে',
  newMatches: 'নতুন ম্যাচ',
  searchMatches: 'ম্যাচ খুঁজুন...',
  noMatchesYet: 'এখনো কোনো ম্যাচ নেই',
  noMatchesDesc: 'সোয়াইপ করতে থাকুন — আপনার ম্যাচ অপেক্ষা করছে।',
  startSwiping: 'সোয়াইপ শুরু করুন',
  messages: 'বার্তা',
  unread: 'অপঠিত',
  searchConversations: 'কথোপকথন খুঁজুন...',
  noConversations: 'এখনো কোনো কথোপকথন নেই',
  viewMatches: 'আপনার ম্যাচ দেখুন →',
  typeMessage: 'বার্তা লিখুন…',
  profile: 'প্রোফাইল',
  discovery: 'আবিষ্কার',
  notifications: 'বিজ্ঞপ্তি',
  account: 'অ্যাকাউন্ট',
  photos: 'ছবি',
  basicInfo: 'মূল তথ্য',
  displayName: 'প্রদর্শনী নাম',
  bio: 'বায়ো',
  jobTitle: 'পেশা',
  height: 'উচ্চতা',
  education: 'শিক্ষা',
  hometown: 'জন্মস্থান',
  interests: 'আগ্রহ',
  saveChanges: 'পরিবর্তন সংরক্ষণ করুন',
  saving: 'সংরক্ষণ হচ্ছে…',
  unlockPotential: 'আপনার পূর্ণ সম্ভাবনা উন্মুক্ত করুন',
  subscriptionDesc: 'কে আপনাকে পছন্দ করেছে দেখুন, সীমাহীন সোয়াইপ এবং প্রিমিয়াম সুবিধা।',
  usedSwipes: 'আপনি ১০টি বিনামূল্যে সোয়াইপ ব্যবহার করেছেন',
  usedSwipesDesc: 'সোয়াইপ চালিয়ে যেতে এবং কে আপনাকে পছন্দ করেছে দেখতে প্রাইমে আপগ্রেড করুন।',
  peopleLikedYou: 'যারা আপনাকে পছন্দ করেছে',
  waiting: 'অপেক্ষায়',
  choosePlan: 'আপনার প্ল্যান বেছে নিন',
  perMonth: '/মাস',
  startSwiping2: 'সোয়াইপ শুরু করুন 🌹',
  seeWhoLiked: 'কে পছন্দ করেছে দেখুন →',
  welcomeToPrime: 'প্রাইমে স্বাগতম',
  primeActive: 'আপনার সাবস্ক্রিপশন এখন সক্রিয়।',
  navDiscover: 'আবিষ্কার',
  navMatches: 'ম্যাচ',
  navMessages: 'বার্তা',
  navProfile: 'প্রোফাইল',
};

const translations: Record<Language, Translations> = { en, bn };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: en,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
